import { DIFFICULTY_CONFIG } from '../../../config/difficulty.config';
import { DOZER_CONFIG } from '../../../config/dozer.config';
import { LEVEL_CONFIG } from '../../../config/level.config';
import { PLAYER_CONFIG } from '../../../config/player.config';
import { RENDERING_CONFIG } from '../../../config/rendering.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DifficultyManager } from '../../../difficulty/DifficultyManager';
import { dozerSpan } from '../../../dozer/dozerRules';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { deriveJumpPhysics } from '../../../physics/JumpPhysics';
import { jumpHeightAt } from '../../../physics/JumpReach';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  jumpArc,
  laneArea,
  laneCenterX,
  moduleSides,
  obstacleRowOn,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import {
  SegmentKind,
  type CollectibleSpawnPoint,
  type FixedCollectiblePlacement,
  type FixedObstaclePlacement,
  type ObstacleSpawnPoint,
  type ObstacleVariant,
  type SegmentDefinition,
} from '../SegmentDefinition';

/**
 * SEG_DOZER_60M – "Planierraupe: Grube zuschieben" (user 2026-10-02: "darauf springen … Erde vor sich herschieben
 * … dass man eine Belohnung bekommt" – "den bauen wir alle 600 meter ein ca."). Scheduled set piece: once at each mark
 * of DOZER_SET_PIECE.placement – 700, 1300, 1900 m … every 600 m. Where a mark meets a golden-ramp mark (500, 1000,
 * 1500, 2000, then every 1000 m) the dozer gives way (priority, LevelConfig.setPieces.minGap): later, never skipped.
 *
 *   z 0–10     approach: the dozer (VEH_DOZER_A, 4.2 m) idles in the middle lane, rear face at z 10.1, a bottle
 *              line leads up the single-jump arc onto its seat. Lanes 0 and 2 run past it – on foot it is a lane
 *              change (OBS_DOZER_A): running into it is a frontal hit, changing lanes into it a stumble.
 *   mount      a jump that touches it with the feet within a step of the track top (≥ 0.49 m) gets on – 9.8 m of
 *              the module's own run-up leave ≥ 0.47 s of take-off timing at 18 m/s (tests/unit/dozer.test.ts)
 *   z 14–46    driving (blade 14.1 → 46.75, the seat ≈ 35 m): three rows 10.5 m apart (reaction + one lane change
 *              of the dozer at 15.3 m/s = 10.1 m) with a heap of earth each (pushed: the pile grows) and one or two
 *              concrete obstacles the blade cannot move (block, pipe – a hit throws the rider off)
 *   z 47–52    the pit across all lanes, 5 m like SEG_PIT_SMALL – a single jump on foot. With ≥ 2 heaps on the
 *              blade the pile fills the dozer's lane and the dozer drives over it; with less it stops at the edge
 *              and the rider is catapulted over the pit (no life lost)
 *   z 52–60    run-out: the ride ends over the filled pit with a jump off, the dozer brakes behind the runner
 *
 * Every row is a jump for a runner on foot (heap 0.9 m, block 0.88 m, pipe 1.12 m) – the module is playable
 * without the dozer too. The layouts alternate the side of the slalom; the heaps lie where one lane change per row
 * reaches all of them (the "perfect" run).
 */
export const DOZER_SET_PIECE = {
  length: 60,
  /** Lane the dozer is parked in. */
  lane: 1,
  /** Origin of the parked dozer (ground centre of its tracks). */
  parkZ: 12,
  spawnPointId: 'SOCKET_OBS_DOZER',
  /** The pit across all lanes: 5 m = the single-jump limit at start speed (LayoutRules.maxJumpGap), depth like SEG_PIT_SMALL. */
  pit: { id: 'GAP_PIT', zFrom: 47, zTo: 52, depth: 2.5 },
  /** Obstacle rows (z): 10.5 m apart where the dozer changes lanes, 7 m where it keeps its lane. */
  rows: { R19: 19.5, R26: 26.5, R30: 30, R37: 37, R40: 40.5 },
  collectibleSpacing: 2,
  /** Bottles over the pit: denser, along a jump arc (extra room before and after it) – like SEG_PIT_SMALL. */
  pitArc: { spacing: 1.5, overhang: 1.5 },
  /** Invitation bottles up the jump arc onto the seat: one per meter. */
  inviteSpacing: 1,
  placement: {
    /**
     * Marks (run distance, m): every 600 m (user 2026-10-02: "alle 600 meter ca."), the first at 700 m – after the
     * warm-up tier (up to 500 m) and the first golden ramp (500 m + its window + 100 m gap), and 200 m before the
     * second one (1000 m), so 700 and 1300 m fit between the golden marks of the first 2 km. With golden marks every
     * 1000 m one dozer mark in five lands within 100 m before a golden mark (1900, 4900, 7900 m …) and waits until
     * the ramp stands (≈ +300 m), one just after (3100, 6100 m …: ≈ +100 m) – every mark is served, in order.
     */
    // user 2026-10-05: far less often – "alle 1.000, 1.200 Meter": every 1100 m (600, 1700, 2800 m … – a mark inside the
    // ructec-Bulli ride would be left out)
    stretches: [{ from: 600, every: 1100 }],
    /** Like the golden ramp: it starts right after its mark; past the window it no longer waits for the open straight. */
    window: 200,
    forceAfter: 60,
    /** Open straight before the module: with its own 10 m of approach the dozer is seen 30 m ahead. */
    sightLine: 0,
    /** Gives way to the golden ramp (priority 1) where the schedules meet. */
    priority: 0,
  },
} as const;

const { lane: DOZER_LANE, parkZ, pit, rows, placement } = DOZER_SET_PIECE;
const SPAN = dozerSpan(DOZER_CONFIG.vehicle.colliders);
const HALF_DEPTH = PLAYER_CONFIG.collider.depth / 2;
const PHYSICS = deriveJumpPhysics(PLAYER_CONFIG);
const FIRST_MARK = placement.stretches[0].from;
/** The invitation line shows the jump at the speed the dozer is first met (first mark). */
const LINE_SPEED = new DifficultyManager(DIFFICULTY_CONFIG).speedAt(FIRST_MARK);
/** Rear face of the parked dozer (segment-local z). */
export const DOZER_FACE_Z = parkZ + SPAN.rear;

// ── Obstacles ────────────────────────────────────────────────────────────────

const LANES = [0, 1, 2] as const;
/** Heaps (OBS_DIRT_MOUND_A 0.9 m), concrete block (0.88 m) and pipe (1.12 m): single jumps on foot. */
const ROW_TYPES = [ObstacleType.Jump] as const;
const DOZER_POINT: ObstacleSpawnPoint = {
  id: DOZER_SET_PIECE.spawnPointId,
  row: 'DOZER',
  lane: DOZER_LANE,
  position: { x: laneCenterX(WORLD_CONFIG, DOZER_LANE), y: WORLD_CONFIG.groundHeight, z: parkZ },
  allowedTypes: [ObstacleType.LaneChange],
};
const spawnPoints: ObstacleSpawnPoint[] = [DOZER_POINT, ...Object.entries(rows).flatMap(([row, z]) => obstacleRowOn(WORLD_CONFIG, row, z, LANES, ROW_TYPES))];

const HEAP = DOZER_CONFIG.heapObstacleId;
const BLOCK = 'OBS_CONCRETE_BLOCK_A';
const PIPE = 'OBS_CONCRETE_PIPE_A';
const DOZER: FixedObstaclePlacement = { spawnPointId: DOZER_POINT.id, obstacleId: DOZER_CONFIG.vehicle.obstacleId };
const at = (row: keyof typeof rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({ spawnPointId: `SOCKET_OBS_${row}_L${lane}`, obstacleId });

/** Slalom: the first heap ahead of the blade, then out to one side and back – the block and the pipe in the way. */
function slalom(id: string, side: 0 | 2): ObstacleVariant {
  return {
    id,
    weight: 1,
    placements: [DOZER, at('R19', DOZER_LANE, HEAP), at('R30', DOZER_LANE, BLOCK), at('R30', side, HEAP), at('R40', side, PIPE), at('R40', DOZER_LANE, HEAP)],
  };
}

/**
 * Two heaps straight ahead, then out to one side round the pipe. No hard obstacle beside the second heap: a driver
 * who swerved after the first one meets it only 7 m later – too close to steer back (reaction + 0.36 s).
 */
function middleFirst(id: string, side: 0 | 2): ObstacleVariant {
  return {
    id,
    weight: 1,
    placements: [DOZER, at('R19', DOZER_LANE, HEAP), at('R26', DOZER_LANE, HEAP), at('R37', DOZER_LANE, PIPE), at('R37', side, HEAP)],
  };
}

// ── Bottles ──────────────────────────────────────────────────────────────────

/**
 * The invitation (fixed, every time): along the middle lane, then up the single jump whose apex (1.5 m ≈ the seat
 * at 1.48 m) meets the rear face – at the speed of the first mark – and one bottle on the seat.
 */
function invitation(): CollectibleSpawnPoint[] {
  const x = laneCenterX(WORLD_CONFIG, DOZER_LANE);
  const apex = PHYSICS.jumpVelocity / PHYSICS.gravity;
  const takeOff = DOZER_FACE_Z - HALF_DEPTH - LINE_SPEED * apex;
  const { collectibleSpacing, inviteSpacing } = DOZER_SET_PIECE;
  const points: { z: number; y: number }[] = [];
  for (let z = 1; z < takeOff - 1e-6; z += collectibleSpacing) points.push({ z, y: COLLECTIBLE_FLOAT_HEIGHT });
  for (let z = takeOff + inviteSpacing; z <= DOZER_FACE_Z + 1e-6; z += inviteSpacing) {
    const feet = jumpHeightAt(PHYSICS, (z - takeOff) / LINE_SPEED, Infinity, RENDERING_CONFIG.fixedTimeStep);
    points.push({ z, y: COLLECTIBLE_FLOAT_HEIGHT + Math.max(0, feet) });
  }
  points.push({ z: parkZ + DOZER_CONFIG.vehicle.seat.z, y: DOZER_CONFIG.vehicle.seat.y + COLLECTIBLE_FLOAT_HEIGHT });
  return points.map((p, i) => ({ id: `SOCKET_COL_INVITE_${String(i + 1).padStart(2, '0')}`, lane: DOZER_LANE, position: { x, y: p.y, z: p.z } }));
}

/** Bottle lines beside the dozer (lanes 0 and 2): straight, an arc over the pit, straight again – over the rows the populator lifts them. */
function sideLines(): CollectibleSpawnPoint[] {
  const flat = () => COLLECTIBLE_FLOAT_HEIGHT;
  const { collectibleSpacing, pitArc, length } = DOZER_SET_PIECE;
  const center = (pit.zFrom + pit.zTo) / 2;
  const half = (pit.zTo - pit.zFrom) / 2 + pitArc.overhang;
  const arc = jumpArc(COLLECTIBLE_FLOAT_HEIGHT, LEVEL_CONFIG.collectibleJumpArcHeight, center, half);
  return LANES.filter((lane) => lane !== DOZER_LANE).flatMap((lane) =>
    collectibleLine(WORLD_CONFIG, `LANE_L${lane}`, [
      { lane, from: 1, to: center - half - pitArc.spacing, spacing: collectibleSpacing, height: flat },
      { lane, from: center - half, to: center + half, spacing: pitArc.spacing, height: arc },
      { lane, from: center + half + collectibleSpacing, to: length - 1, spacing: collectibleSpacing, height: flat },
    ]),
  );
}

const INVITATION = invitation();
const FIXED_BOTTLES: FixedCollectiblePlacement[] = INVITATION.map((point) => ({ spawnPointId: point.id, collectibleId: 'COL_BEER_BOTTLE_A' }));

export const SEG_DOZER_60M: SegmentDefinition = {
  id: 'SEG_DOZER_60M',
  kind: SegmentKind.Straight,
  displayName: 'Planierraupe – Grube zuschieben 60 m',
  length: DOZER_SET_PIECE.length,
  width: WORLD_CONFIG.trackWidth,
  // One Blender module over the whole 60 m: road, the shored pit across all lanes, barriers, fence
  // (tools/blender/segments/SEG_24_Dozer_Pit.py).
  visualAssetId: 'SEG_DOZER_60M',

  entry: entryConnector(),
  exits: [straightExit(DOZER_SET_PIECE.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: {
    surfaces: [],
    gaps: [{ id: pit.id, ...laneArea(WORLD_CONFIG, 0, LANES.length - 1, pit.zFrom, pit.zTo), depth: pit.depth }],
    ceilings: [],
  },
  dozer: { spawnPointId: DOZER_POINT.id, pitGapId: pit.id },

  obstacleSpawnPoints: spawnPoints,
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [slalom('SLALOM_LEFT', 0), slalom('SLALOM_RIGHT', 2), middleFirst('MIDDLE_THEN_LEFT', 0), middleFirst('MIDDLE_THEN_RIGHT', 2)],

  collectibleSpawnPoints: [...INVITATION, ...sideLines()],
  fixedCollectibles: FIXED_BOTTLES,

  ...moduleSides(DOZER_SET_PIECE.length),

  generation: {
    // unused: the schedule places the module (LevelGenerator) – it is never picked at random
    weight: 1,
    minDistance: FIRST_MARK,
    minSegmentsAfterJunction: 1,
    family: 'DOZER',
    schedule: { ...placement, stretches: [...placement.stretches] },
    tags: ['setpiece', 'dozer', 'vehicle', 'terrain', 'pit'],
  },
};
